import { useMutation } from '@apollo/client';
import { useEffect, useMemo } from 'react';

import type { PageViewTrackingEvent } from '@xing-com/crate-communication-tracking';
import { useTracking } from '@xing-com/crate-communication-tracking';
import { useLocation } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { JDP_SUMMARY_MAX_SKILLS } from '@xing-com/crate-jobs-constants';
import type { JobPostingSkillFragment } from '@xing-com/crate-jobs-graphql';
import { RegisterVisitDocument } from '@xing-com/crate-jobs-graphql';
import type {
  JobDetails,
  Preference,
  UserJobPreferencesData,
} from '@xing-com/crate-jobs-helpers';
import {
  getHighlightsTrackingData,
  getRevenueTypeContextDimension,
  getSalaryTypeContextDimension,
  hasMatchingSignifier,
  matchJobWithPreferences,
} from '@xing-com/crate-jobs-helpers';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { useExperimentTrackingData, useViewerData } from '@xing-com/hub';

type Props = {
  job: JobDetails;
  modulesList: string;
  userJobPreferences?: UserJobPreferencesData; // used for adobe tracking only
  matchedJobPreferences?: Preference[]; // used for nwt tracking
  matchedSkills?: JobPostingSkillFragment[];
  skip: boolean;
};

const getUserJobPreferencesTrackingData = (
  job: JobDetails,
  userJobPreferences?: UserJobPreferencesData
): string => {
  const matchingPreferencesCount = userJobPreferences
    ? matchJobWithPreferences(job, userJobPreferences).length
    : 0;

  return `matching_preferences_${
    matchingPreferencesCount > 0 ? matchingPreferencesCount : 'no_visible'
  }`;
};

const getMatchingSkillsTrackingData = (
  matchingSkills?: JobPostingSkillFragment[]
): string => {
  const matchingSkillsCount = Math.min(
    (matchingSkills ?? []).length,
    JDP_SUMMARY_MAX_SKILLS
  );

  return `matching_skills_${
    matchingSkillsCount > 0 ? matchingSkillsCount : 'no_visible'
  }`;
};

const getMatchingSignifierTrackingData = (
  job: JobDetails,
  userJobPreferences?: UserJobPreferencesData,
  matchedSkills?: JobPostingSkillFragment[]
): string | undefined => {
  const matchingPreferences = userJobPreferences
    ? matchJobWithPreferences(job, userJobPreferences)
    : [];
  return hasMatchingSignifier(matchingPreferences, matchedSkills)
    ? 'matching_signifier'
    : undefined;
};

export const useJobDetailsTracking = ({
  job,
  modulesList,
  userJobPreferences,
  matchedJobPreferences,
  matchedSkills,
  skip,
}: Props): void => {
  const { isLoggedIn } = useLoginState();
  const location = useLocation();
  const viewerData = useViewerData();
  const { getSearchParameter } = useSearchParameter();
  const { track: trackView } = useTracking<PageViewTrackingEvent>();
  const trackNwt = useJobsNwtTracking();
  const [registerVisitMutation] = useMutation(RegisterVisitDocument);
  const experimentTrackingData = useExperimentTrackingData();

  const highlightsTrackingData = getHighlightsTrackingData({
    highlights: job.highlights,
    salary: job.salary,
    isVerifiedByXing: !!job.facts.isVerifiedByXing,
  });
  const userJobPreferencesTrackingData = getUserJobPreferencesTrackingData(
    job,
    userJobPreferences
  );
  const matchingSkillsTrackingData =
    getMatchingSkillsTrackingData(matchedSkills);
  const matchingSignifierTrackingData = getMatchingSignifierTrackingData(
    job,
    userJobPreferences,
    matchedSkills
  );

  const adobeViewTrackingBaseProps = useMemo(() => {
    const tttSpParam = getSearchParameter('ttt_sp');

    return {
      channel: 'wbm/Stellenmarkt',
      page: `wbm/Stellenmarkt/detail${job.redirectsToThirdPartyUrl ? '_external' : ''}`,
      PropJobsId: job.id,
      PropContextDimension2: getRevenueTypeContextDimension(job),
      PropContextDimension3: getSalaryTypeContextDimension(job.salary),
      PropContextDimension5: `jobs_neffi_score_${viewerData.data?.networkEfficiencyIndicator?.score}`,
      ...(job.discipline?.id && { PropJobsDiscipline: job.discipline.id }),
      PropModules: modulesList,
      ...(tttSpParam && { PropSearchPosition: tttSpParam }),
      ...experimentTrackingData,
    };
  }, [
    experimentTrackingData,
    getSearchParameter,
    job,
    modulesList,
    viewerData.data?.networkEfficiencyIndicator?.score,
  ]);

  const adobeViewTrackingContextProps = useMemo(() => {
    const scOPropActionOriginParam = getSearchParameter(
      'sc_o_PropActionOrigin'
    );

    return {
      EventJobsDetailView: 1,
      PropContextAddition: [
        highlightsTrackingData,
        userJobPreferencesTrackingData,
        matchingSkillsTrackingData,
        matchingSignifierTrackingData,
      ]
        .filter((item) => !!item)
        .join(','),
      ...(scOPropActionOriginParam && {
        PropActionOrigin: scOPropActionOriginParam,
      }),
    };
  }, [
    getSearchParameter,
    highlightsTrackingData,
    matchingSignifierTrackingData,
    matchingSkillsTrackingData,
    userJobPreferencesTrackingData,
  ]);

  const nwtFlags = isLoggedIn
    ? `matching_preferences_${matchedJobPreferences ? matchedJobPreferences.length : 0},matching_skills_${matchedSkills ? matchedSkills.length : 0}`
    : undefined;

  useEffect(() => {
    if (!skip) {
      const page = `jobs/detail${job.redirectsToThirdPartyUrl ? '_external' : ''}`;
      const context = job.redirectsToThirdPartyUrl ? 'no_redirect' : undefined;
      trackNwt({
        eventSchema: 'basic',
        event: 'viewed_screen',
        page,
        itemUrn: `surn:x-xing:jobs:posting:${job.id}`,
        flags: nwtFlags,
        context,
      });

      trackNwt({
        eventSchema: 'basic',
        event: 'job_detail_viewed',
        page,
        itemUrn: `surn:x-xing:jobs:posting:${job.id}`,
        flags: nwtFlags,
        context,
      });

      trackView({
        type: 'pageview',
        ...adobeViewTrackingBaseProps,
        contextProps: { ...adobeViewTrackingContextProps },
      });

      registerVisitMutation({
        variables: {
          jobId: job.id,
          originalUrl: location.href,
          referrer: document.referrer,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);
};
